import {
  IPerioChartData,
  PERIO_SECTIONS,
  PerioChartSectionData,
  PerioSection,
} from '@dataTypes/chartingFeature.types'

export const getToothIndex = (tooth: number): number => tooth - 1

export type PerioProbeSite = 'D' | 'B' | 'M'

/** Teeth 9–24 probe left→right as mesial → buccal → distal (MBD). */
export const usesMbdProbeOrder = (tooth: number): boolean =>
  tooth >= 9 && tooth <= 24

export const getProbeSiteLabels = (tooth: number): PerioProbeSite[] =>
  usesMbdProbeOrder(tooth) ? ['M', 'B', 'D'] : ['D', 'B', 'M']

/** Reorder anatomic storage parts (D B M) into probe/display order. */
export const toDisplayOrder = (
  tooth: number,
  storageParts: string[],
): string[] => {
  if (storageParts.length !== 3 || !usesMbdProbeOrder(tooth))
    return [...storageParts]
  return [storageParts[2], storageParts[1], storageParts[0]]
}

/** Reorder probe/display parts into anatomic storage order (D B M). */
export const toStorageOrder = (
  tooth: number,
  displayParts: string[],
): string[] => {
  if (displayParts.length !== 3 || !usesMbdProbeOrder(tooth))
    return [...displayParts]
  return [displayParts[2], displayParts[1], displayParts[0]]
}

export const createEmptyPerioChartData = (): IPerioChartData => {
  const rowTriple = (): string[] => Array(32).fill('0 0 0')
  const rowSingle = (): string[] => Array(32).fill('0')

  const createSection = (): PerioChartSectionData => ({
    PD: rowTriple(),
    GM: rowTriple(),
    CAL: rowTriple(),
    Bleeding: rowTriple(),
    Calculus: rowTriple(),
    Supp: rowTriple(),
    Furcation: rowTriple(),
    Mobility: rowSingle(),
  })

  return {
    [PerioSection.Facial]: createSection(),
    [PerioSection.Lingual]: createSection(),
  }
}

/** Detach every row, including drafts created before the surfaces were independent. */
export const clonePerioChartData = (
  data: IPerioChartData = createEmptyPerioChartData(),
): IPerioChartData => {
  const result = createEmptyPerioChartData()
  for (const section of PERIO_SECTIONS) {
    for (const metric of Object.keys(result[section]) as Array<
      keyof PerioChartSectionData
    >) {
      const row = data[section]?.[metric]
      if (row) result[section][metric] = [...row]
    }
  }
  return result
}
